import { useRef, useState } from "react";
import { useUpdateEffect } from "../../../common/hooks";
import {
  OptionComparatorType,
  OptionsType,
  OptionType,
  SelectProps,
} from "../../types";
import { changeOptionToUnchecked, getCheckedOptions } from "../../utils";
import { useSelectMode } from "../use-select-mode";
import {
  checkOptionForMultipleMode,
  checkOptionForSingleMode,
  getOptionsFromProps,
} from "./utils";

export const useOptionsState = ({
  options: optionsFromProps,
  type,
  onChange,
}: Pick<SelectProps, "onChange" | "type" | "options">) => {
  const { isMultipleMode, isSingleMode } = useSelectMode(type);

  const [options, setOptions] = useState<OptionsType>(
    getOptionsFromProps(optionsFromProps, isSingleMode)
  );

  const [currentUpdatedOption, setCurrentUpdatedOption] =
    useState<OptionType>();

  const wasOptionsFromPropsUpdated = useRef(false);

  const updateCheckedOptions = (
    option: OptionType,
    comparator: OptionComparatorType
  ) => {
    setCurrentUpdatedOption({
      ...option,
      checked: isMultipleMode ? !option.checked : true,
    });

    if (isSingleMode) {
      setOptions(checkOptionForSingleMode(options, option, comparator));
    }

    if (isMultipleMode) {
      setOptions(checkOptionForMultipleMode(options, option, comparator));
    }
  };

  const clearOptions = () => {
    setCurrentUpdatedOption(undefined);
    setOptions(changeOptionToUnchecked(options));
  };

  useUpdateEffect(() => {
    wasOptionsFromPropsUpdated.current = true;
    const newOptionsFromProps = getOptionsFromProps(
      optionsFromProps,
      isSingleMode
    );

    setOptions(newOptionsFromProps);
  }, [optionsFromProps]);

  useUpdateEffect(() => {
    const checkedOptions = getCheckedOptions(options);

    if (wasOptionsFromPropsUpdated.current) {
      wasOptionsFromPropsUpdated.current = false;
    } else {
      onChange?.(checkedOptions, currentUpdatedOption);
    }
  }, [options]);

  return {
    options,
    clearOptions,
    updateCheckedOptions,
  };
};
